import React, { useState, useEffect, useMemo } from "react";
import { AddClient } from "./addClient";
import axios from "axios";
import "./landing.css";
import { Header } from "../../../components/header/header";
import { StatCard } from "../../../components/statCard/statCard";
import { useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import api from "../../../api";

// ── sort helpers ─────────────────────────────────────────────────────────────
const COLUMNS = [
  { key: "Party",              label: "PARTY",          type: "text" },
  { key: "Bal Sale Potential", label: "BALANCE SALE",   type: "number" },
  { key: "Reconnect Date",     label: "RE-CONNECT ON",  type: "date" },
  { key: "Os Amt",             label: "AMOUNT OVERDUE", type: "number" },
];

const sortRows = (rows, col, dir) => {
  if (!col) return rows;
  return [...rows].sort((a, b) => {
    const av = a[col] ?? (typeof a[col] === "number" ? 0 : "");
    const bv = b[col] ?? (typeof b[col] === "number" ? 0 : "");
    if (av === null || av === "") return 1;
    if (bv === null || bv === "") return -1;
    let cmp = 0;
    if (typeof av === "number" && typeof bv === "number") {
      cmp = av - bv;
    } else if (col === "Reconnect Date") {
      cmp = new Date(av) - new Date(bv);
    } else {
      cmp = String(av).localeCompare(String(bv));
    }
    return dir === "asc" ? cmp : -cmp;
  });
};

export const Landing = () => {
  const navigate = useNavigate();
  const [apiResponse, setApiResponse] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [showAddClient, setShowAddClient] = useState(false);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [selectedCat, setSelectedCat] = useState(""); // "" = All

  const salesPersonId = localStorage.getItem("salesPersonId");

  const fetchLandingPage = async (categoryId = "") => {
    try {
      const params = { SalespersonId: salesPersonId };
      if (categoryId) params.CategoryId = categoryId;
      const response = await api.get("/api/landingpage", { params });
      setApiResponse(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchLandingPage();
  }, []);

  const partySales = apiResponse?.data?.partySales || [];
  const categoryList = apiResponse?.data?.categoryList || [];

  const handleLogout = () => {
    localStorage.removeItem("salesPersonId");
    navigate("/login");
  };

  // ── fuse search ──
  const fuse = useMemo(
    () =>
      new Fuse(partySales, {
        keys: ["Party"],
        threshold: 0.2,
        ignoreLocation: true,
      }),
    [partySales]
  );

  const searched = searchText.trim()
    ? fuse.search(searchText).map((r) => r.item)
    : partySales;

  // ── sort ──
  const sorted = useMemo(
    () => sortRows(searched, sortCol, sortDir),
    [searched, sortCol, sortDir]
  );

  // ── column header click ──
  const handleSort = (key, type) => {
    if (sortCol === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(key);
      setSortDir(type === "number" ? "desc" : "asc");
    }
  };

  const sortIcon = (key) => {
    if (sortCol !== key) return "unfold_more";
    return sortDir === "asc" ? "arrow_upward" : "arrow_downward";
  };

  if (!apiResponse) return <div>Loading...</div>;

  return (
    <div className="landingMain">
      <Header pageTitle="Clients Directory" showLogout onLogout={handleLogout} />

      <div className="landingContentMain">
        {/* Search + Add Client */}
        <div className="landingSearchWrapper">
          <div className="landingSearchDiv">
            <span
              style={{ color: "white", fontSize: "2vh", placeSelf: "center" }}
              className="material-symbols-outlined"
            >
              search
            </span>
            <input
              className="landingSearchInputBar"
              placeholder="Search clients..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className="landingAddClientWrapper">
            <div
              className="landingAddClientDiv"
              onClick={() => setShowAddClient(true)}
            >
              <span
                style={{ color: "white", fontSize: "1.8vh", placeSelf: "center" }}
                className="material-symbols-outlined"
              >
                add
              </span>
              <p className="addClientButtonText">Add Client</p>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="landingStatCardsDiv">
          <StatCard
            title="Current Month Sale Value"
            netSale={apiResponse.data.monthlySalesSummary.ItemValue}
            grossSale={apiResponse.data.monthlySalesSummary.InvoiceAmt}
          />
          <StatCard
            title="Total Outstanding"
            value={apiResponse.data.receivableSummary["Total Receivable"]}
          />
          <StatCard
            title="Overdue"
            value={apiResponse.data.receivableSummary.Overdue}
          />
          <StatCard
            title="Quantity"
            value={apiResponse.data.monthlySalesSummary.TotQty}
          />
          <StatCard
            title="Current Month Receipt"
            value={apiResponse.data.currentReceipt["Current Receipt"]}
          />
        </div>

        {/* Clients + Category */}
        <div className="landingClientsWrapper">
          {/* ── Clients table ── */}
          <div className="landingClientsDivWrapper">
            <div className="landingClientsDiv">
              <div className="landingClientsHeaderDiv">
                <p className="clientsHeaderText">Clients</p>
              </div>

              <div className="clientsTableWrapper">
                <table className="clientsTable">
                  <thead className="clientsTableHeader">
                    <tr>
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className="clientsTableSortTh"
                          onClick={() => handleSort(col.key, col.type)}
                        >
                          <div className="clientsThInner">
                            {col.label}
                            <span
                              className="material-symbols-outlined clientsSortIcon"
                              data-active={sortCol === col.key ? "true" : "false"}
                            >
                              {sortIcon(col.key)}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody key={searchText + sortCol + sortDir}>
                    {sorted.map((item, index) => (
                      <tr
                        className="clientsTableRow"
                        key={index}
                        onClick={() =>
                          navigate(`/details/${item.PartyId}`, {
                            state: {
                              partyName: item.Party,
                              partyPhone: item.ParPhone || null,
                              partyEmail: item.ParEmail || null,
                            },
                          })
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <td className="partyNameText">{item.Party}</td>

                        <td style={{ color: "white" }}>
                          {item["Bal Sale Potential"]
                            ? `₹${item["Bal Sale Potential"].toLocaleString("en-IN")}`
                            : "-"}
                        </td>

                        <td style={{ color: "white" }}>
                          {item["Reconnect Date"]
                            ? new Date(item["Reconnect Date"]).toLocaleDateString("en-IN")
                            : "-"}
                        </td>

                        <td style={{ color: item["Os Amt"] > 0 ? "#ef4444" : "white" }}>
                          {item["Os Amt"]
                            ? `₹${item["Os Amt"].toLocaleString("en-IN")}`
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Category panel ── */}
          <div className="landingCategoryWrapper">
            <p className="landingCategoryTitle">Category Selection</p>
            <p className="landingCategorySubtitle">
              On Selection show All details only for that Category
            </p>
            <div className="landingCategorySelectWrapper">
              <select
                className="landingCategorySelect"
                value={selectedCat}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedCat(value);
                  fetchLandingPage(value);
                }}
              >
                <option value="">All Categories</option>
                {categoryList.map((cat) => (
                  <option key={cat.CategoryId} value={cat.CategoryId}>
                    {cat.CategoryName}
                  </option>
                ))}
              </select>
              <span className="material-symbols-outlined landingCategoryArrow">
                expand_more
              </span>
            </div>
          </div>
        </div>
      </div>

      {showAddClient && <AddClient onClose={() => setShowAddClient(false)} />}
    </div>
  );
};